import React, { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { crmService } from '@/lib/api';
import CrmHeader from './CrmHeader';
import CrmContacts from './CrmContacts';
import CrmInteractionForm from './CrmInteractionForm';
import CrmTimeline from './CrmTimeline';
import CrmTasks from './CrmTasks';

const DebtorCrmSheet = ({ debtorId, open, onClose, onChanged, tenantSlug, currentUserEmail }) => {
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!debtorId) return;
    try {
      const [ficha, team] = await Promise.all([crmService.debtor(debtorId), crmService.users()]);
      setData(ficha.data); setUsers(team.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cargar la ficha');
    }
  }, [debtorId]);

  useEffect(() => {
    if (open && debtorId) { setLoading(true); setData(null); load().finally(() => setLoading(false)); }
  }, [open, debtorId, load]);

  const refresh = () => { load(); onChanged?.(); };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent className="sm:max-w-3xl w-full p-0 flex flex-col" data-testid="crm-sheet">
        <SheetHeader className="px-5 pt-5 pb-0">
          <SheetTitle className="sr-only">Ficha del titular</SheetTitle>
          <SheetDescription className="sr-only">Gestiones, contactos y agenda</SheetDescription>
          {data && <CrmHeader data={data} onChanged={refresh} tenantSlug={tenantSlug} />}
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-5 pb-6">
          {loading && <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>}
          {data && (
            <Tabs defaultValue="gestiones" className="mt-3">
              <TabsList className="grid grid-cols-3 w-full">
                <TabsTrigger value="gestiones" data-testid="crm-tab-gestiones">Gestiones ({data.interactions.length})</TabsTrigger>
                <TabsTrigger value="contactos" data-testid="crm-tab-contactos">Contactos ({data.contacts.length})</TabsTrigger>
                <TabsTrigger value="agenda" data-testid="crm-tab-agenda">Agenda ({data.tasks.filter((t) => t.status === 'pending').length})</TabsTrigger>
              </TabsList>
              <TabsContent value="gestiones" className="space-y-4 mt-3">
                <CrmInteractionForm debtorId={debtorId} contacts={data.contacts} settings={data.settings} users={users} currentUserEmail={currentUserEmail} onCreated={refresh} />
                <CrmTimeline interactions={data.interactions} />
              </TabsContent>
              <TabsContent value="contactos" className="mt-3">
                <CrmContacts debtorId={debtorId} contacts={data.contacts} onChanged={refresh} />
              </TabsContent>
              <TabsContent value="agenda" className="mt-3">
                <CrmTasks debtorId={debtorId} tasks={data.tasks} users={users} currentUserEmail={currentUserEmail} onChanged={refresh} />
              </TabsContent>
            </Tabs>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default DebtorCrmSheet;
